import { useState, useEffect, type ReactNode } from 'react'
import Logo from '../common/Logo'

const backgroundImages = [
  '/images/backgrounds/bg-1.png',
  '/images/backgrounds/bg-2.png',
  '/images/backgrounds/bg-3.png',
  '/images/backgrounds/bg-4.png',
  '/images/backgrounds/bg-5.png',
  '/images/backgrounds/bg-6.png',
  '/images/backgrounds/bg-7.png',
  '/images/backgrounds/bg-8.png',
  '/images/backgrounds/bg-9.png',
  '/images/backgrounds/bg-10.png',
]

interface AuthLayoutProps {
  children: ReactNode
}

export default function AuthLayout({ children }: AuthLayoutProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [imagesLoaded, setImagesLoaded] = useState(false)
  const [isPaused, setIsPaused] = useState(false)

  // Preload all images
  useEffect(() => {
    const preloadImages = async () => {
      const promises = backgroundImages.map((src) => {
        return new Promise<void>((resolve) => {
          const img = new Image()
          img.onload = () => resolve()
          img.onerror = () => resolve()
          img.src = src
        })
      })
      await Promise.all(promises)
      setImagesLoaded(true)
    }
    preloadImages()
  }, [])

  // Slideshow timer (4 seconds)
  useEffect(() => {
    if (!imagesLoaded || isPaused) return
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % backgroundImages.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [imagesLoaded, isPaused])

  return (
    <div
      className="min-h-screen relative overflow-hidden group cursor-pointer"
      onClick={() => setIsPaused(!isPaused)}
    >
      {/* Slideshow Background */}
      <div className="absolute inset-0 -z-10 bg-gray-900">
        {backgroundImages.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
          />
        ))}
      </div>

      {/* Pause/Play Button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          setIsPaused(!isPaused)
        }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 w-10 h-10 bg-white/70 backdrop-blur-xl shadow-lg rounded-full flex items-center justify-center text-black opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"
      >
        {isPaused ? (
          <svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
            <path d="M8 5v14l11-7z" />
          </svg>
        ) : (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        )}
      </button>

      {/* Logo */}
      <div className="absolute top-6 left-6 z-10" onClick={(e) => e.stopPropagation()}>
        <Logo size="lg" inverted to="/" className="drop-shadow-lg" />
      </div>

      {/* Centered content */}
      <div
        className="flex items-center justify-center min-h-screen px-4 py-12"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  )
}
